"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import DOMPurify from "isomorphic-dompurify";
import { usePathname } from "next/navigation";
import Script from "next/script";

import {
  dispatchTrackingEvent,
  GTMEVARunner,
  TrackingEvents,
} from "@jti/tracker";
import { useConfig } from "@jti/ui/providers/useConfig";

import { type PageConfig, usePageConfig } from "@/context/PageConfigContext";

const sanitize = DOMPurify.sanitize;

type GTMRunnerProps = {
  staticPageContext?: Pick<
    PageConfig,
    "analytics" | "pageName" | "pageLoadTracking"
  >;
  postGTMInitAction?: {
    type: TrackingEvents;
    data?: object;
  };
};

export const GTMRunner = ({
  staticPageContext,
  postGTMInitAction,
}: GTMRunnerProps) => {
  const pageConfig = usePageConfig();
  const [pageValues, setPageValues] = useState(pageConfig);
  const { analytics, pageName, pageLoadTracking } = pageValues;
  const pathname = usePathname();
  const gtmContainerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const { GTM_CODE } = useConfig();

  useEffect(() => {
    if (staticPageContext) {
      setPageValues((prev) => ({
        ...prev,
        ...staticPageContext,
      }));
    }

    setMounted(true);
  }, [pathname, staticPageContext]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (mounted && analytics) {
      GTMEVARunner.getInstance(gtmContainerRef.current, "other");
      if (postGTMInitAction) {
        timeout = setTimeout(() => {
          dispatchTrackingEvent(
            postGTMInitAction.type,
            postGTMInitAction?.data as never
          );
        }, 5000);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [
    analytics,
    pageName,
    pageLoadTracking,
    pathname,
    mounted,
    postGTMInitAction,
  ]);

  return (
    <>
      {mounted &&
        analytics &&
        createPortal(
          <div
            id="gtmContainer"
            data-page-name={pageName}
            data-gtm-code={GTM_CODE}
            data-on-load-tracking={pageLoadTracking}
            ref={gtmContainerRef}
          />,
          // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
          document.body
        )}
      <div hidden>
        <Script
          id="gtm-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: sanitize(`(function (w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
              });
              var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
              j.async = true;
              j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', "${GTM_CODE}");`),
          }}
        />
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_CODE}`}
          />
        </noscript>
      </div>
    </>
  );
};
